import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, Eye, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /board-share/:token — public, read-only board snapshot.
 * No auth. Names are obfuscated server-side. Auto-refreshes every 60s so the
 * shared view stays semi-live without websockets.
 */
const PublicBoardShare = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/public/boards/share/${token}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load this board.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050a] text-white flex items-center justify-center" data-testid="public-board-loading">
        <Loader2 size={26} className="animate-spin text-[#22c55e]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#05050a] text-white flex items-center justify-center p-6" data-testid="public-board-error">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#ff0055]/10 border border-[#ff0055]/30 text-[#ff5577] flex items-center justify-center mb-4">
            <AlertCircle size={26} />
          </div>
          <h1 className="font-display font-bold text-2xl mb-1.5">Link unavailable</h1>
          <p className="text-[#a395c3] text-sm mb-6">{error}</p>
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 text-sm hover:bg-white/[0.1]">
            <Home size={12} /> Go to SB Media Studio
          </Link>
        </div>
      </div>
    );
  }

  const { board, cards, expires_at, label } = data;
  const accent = board?.accent || "#22c55e";
  const isKanban = board?.view === "kanban";

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white" data-testid="public-board-page">
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute -top-40 left-1/4 w-[520px] h-[520px] rounded-full blur-[120px]" style={{ background: `${accent}18` }} />
        <div className="absolute bottom-[-15%] right-[10%] w-[420px] h-[420px] rounded-full bg-[#a78bfa]/8 blur-[140px]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: accent }}>
              <Eye size={11} /> Read-only snapshot
              {label && <span className="text-white/50">· {label}</span>}
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
              {board.name}
            </h1>
            {board.description && (
              <p className="text-[#a395c3] text-sm mt-2 max-w-2xl">{board.description}</p>
            )}
          </div>
          <div className="text-[11px] text-white/45">
            Expires {new Date(expires_at).toLocaleDateString()} · auto-refresh every 60s
          </div>
        </div>

        {isKanban ? (
          <div className="flex gap-4 overflow-x-auto pb-6">
            {(board.columns || []).map((col) => {
              const items = cards?.[col.id] || [];
              return (
                <div
                  key={col.id}
                  className="flex-shrink-0 w-[280px] md:w-[300px] rounded-2xl border border-white/10 bg-white/[0.02] p-3"
                  data-testid={`public-col-${col.id}`}
                >
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                      <span className="text-[11px] uppercase tracking-[0.24em] font-semibold">{col.label}</span>
                    </div>
                    <span className="text-[10px] text-white/45">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.length === 0 ? (
                      <div className="text-[11px] text-white/30 italic px-2 py-4 text-center">—</div>
                    ) : items.map((card) => (
                      <div
                        key={`${card.kind}-${card.id}`}
                        data-testid={`public-card-${card.id}`}
                        className="rounded-xl border border-white/10 bg-[#0a0510] p-3"
                        style={{ borderLeft: `3px solid ${accent}` }}
                      >
                        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">{card.kind}</div>
                        <div className="text-sm font-semibold truncate">{card.title}</div>
                        {card.subtitle && (
                          <div className="text-[11px] text-[#a395c3] mt-1 truncate">{card.subtitle}</div>
                        )}
                        {card.preferred_date && (
                          <div className="text-[10px] text-white/50 mt-1.5">{card.preferred_date}</div>
                        )}
                        {card.labels?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {card.labels.map((l) => (
                              <span key={l} className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-white/[0.06] text-white/70">
                                {l}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-[#a395c3] italic">Calendar view isn&apos;t shareable yet — use Kanban boards for public links.</div>
        )}

        <div className="mt-10 text-center text-[11px] text-white/40">
          Powered by <a href="/" className="text-[#00f0ff] hover:text-white">SB Media Studio</a> · Command Center
        </div>
      </div>
    </div>
  );
};

export default PublicBoardShare;
